import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { FaLock, FaEye, FaEyeSlash, FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';

const PasswordChange = () => {
  const [passwords, setPasswords] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { token } = useAuth();
  const { isDarkMode } = useTheme();

  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
    const minLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    
    return {
      isValid: minLength && hasUppercase && hasLowercase && hasNumber,
      minLength,
      hasUppercase,
      hasLowercase,
      hasNumber
    };
  };

  const handleChange = (field, value) => {
    setPasswords(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!passwords.oldPassword || !passwords.newPassword || !passwords.confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (passwords.newPassword !== passwords.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwords.oldPassword === passwords.newPassword) {
      setError('New password must be different from old password');
      return;
    }

    const validation = validatePassword(passwords.newPassword);
    if (!validation.isValid) {
      setError('New password does not meet requirements');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/user/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          old_password: passwords.oldPassword,
          new_password: passwords.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Password changed successfully!');
        setPasswords({
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validation = validatePassword(passwords.newPassword);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-brand-purple to-brand-red mb-4">
          <FaLock className="w-8 h-8 text-white" />
        </div>
        <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Change Password
        </h3>
        <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Keep your account secure with a strong password
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/20 backdrop-blur-sm border border-red-500/30 text-red-100 px-4 py-3 rounded-lg text-sm flex items-center">
          <FaTimesCircle className="mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-500/20 backdrop-blur-sm border border-green-500/30 text-green-100 px-4 py-3 rounded-lg text-sm flex items-center">
          <FaCheckCircle className="mr-2 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Old Password */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${
            isDarkMode ? 'text-gray-200' : 'text-gray-700'
          }`}>
            Current Password
          </label>
          <div className="relative">
            <input
              type={showPasswords.old ? 'text' : 'password'}
              value={passwords.oldPassword}
              onChange={(e) => handleChange('oldPassword', e.target.value)}
              className={`w-full px-4 py-3 pr-12 rounded-lg border transition-all duration-200 ${
                isDarkMode
                  ? 'bg-gray-800/50 border-gray-700/50 text-white focus:border-brand-purple'
                  : 'bg-white border-gray-300 text-gray-900 focus:border-brand-purple'
              } focus:outline-none focus:ring-2 focus:ring-brand-purple/20`}
              placeholder="Enter current password"
            />
            <button
              type="button"
              onClick={() => setShowPasswords(prev => ({ ...prev, old: !prev.old }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPasswords.old ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${
            isDarkMode ? 'text-gray-200' : 'text-gray-700'
          }`}>
            New Password
          </label>
          <div className="relative">
            <input
              type={showPasswords.new ? 'text' : 'password'}
              value={passwords.newPassword}
              onChange={(e) => handleChange('newPassword', e.target.value)}
              className={`w-full px-4 py-3 pr-12 rounded-lg border transition-all duration-200 ${
                isDarkMode
                  ? 'bg-gray-800/50 border-gray-700/50 text-white focus:border-brand-purple'
                  : 'bg-white border-gray-300 text-gray-900 focus:border-brand-purple'
              } focus:outline-none focus:ring-2 focus:ring-brand-purple/20`}
              placeholder="Enter new password"
            />
            <button
              type="button"
              onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>

        {/* Password Requirements */}
        {passwords.newPassword && (
          <div className={`p-4 rounded-lg border ${
            isDarkMode 
              ? 'bg-gray-800/30 border-gray-700/30' 
              : 'bg-gray-50 border-gray-200'
          }`}>
            <p className={`text-xs font-medium mb-2 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Password Requirements:
            </p>
            <div className="space-y-1">
              {[
                { check: validation.minLength, text: 'At least 8 characters' },
                { check: validation.hasUppercase, text: 'One uppercase letter' },
                { check: validation.hasLowercase, text: 'One lowercase letter' },
                { check: validation.hasNumber, text: 'One number' }
              ].map((requirement, idx) => (
                <div key={idx} className="flex items-center text-xs">
                  {requirement.check ? (
                    <FaCheckCircle className="w-3 h-3 text-green-400 mr-2" />
                  ) : (
                    <FaTimesCircle className="w-3 h-3 text-red-400 mr-2" />
                  )}
                  <span className={requirement.check 
                    ? 'text-green-400' 
                    : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }>
                    {requirement.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Confirm Password */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${
            isDarkMode ? 'text-gray-200' : 'text-gray-700'
          }`}>
            Confirm New Password
          </label>
          <div className="relative">
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwords.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              className={`w-full px-4 py-3 pr-12 rounded-lg border transition-all duration-200 ${
                isDarkMode
                  ? 'bg-gray-800/50 border-gray-700/50 text-white focus:border-brand-purple'
                  : 'bg-white border-gray-300 text-gray-900 focus:border-brand-purple'
              } focus:outline-none focus:ring-2 focus:ring-brand-purple/20`}
              placeholder="Confirm new password"
            />
            <button
              type="button"
              onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          {passwords.confirmPassword && passwords.newPassword !== passwords.confirmPassword && (
            <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || !validation.isValid || passwords.newPassword !== passwords.confirmPassword}
          className="w-full py-3 px-4 text-sm font-medium text-white bg-gradient-to-r from-brand-purple to-brand-red hover:from-brand-purple-dark hover:to-brand-red-dark rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loading ? (
            <>
              <FaSpinner className="animate-spin mr-2" />
              Changing Password...
            </>
          ) : (
            'Change Password'
          )}
        </button>
      </form>
    </div>
  );
};

export default PasswordChange;



